import { Injectable } from "@angular/core";
import { ActivatedRoute, NavigationCancel, NavigationExtras, Router } from "@angular/router";
import { AbschnittDisplayModel } from "@/api/models/ace/xFound/Portal/Core/CAQ/Models/abschnitt-display-model";
import { GlobalEnumsAceFormulartyp } from "@/api/models/ace/xFound/Portal/Core/Entities/global-enums-ace-formulartyp";
import { PortalContextService } from "./portal-context.service";
import { ValidationErrors } from "@angular/forms";
import { FormularService } from "./formular.service";
import { ValidationMessage } from "../models/validation-message.model";
import { ValidationFailure } from "@/api/models/FluentValidation/Results/validation-failure";
import { LocalizedTextDisplayModel } from "@/api/model-index";
import { I18nService } from "./i18nService";


export class NavigationParams {
    public UrlSegments: any[];
    public NavExtras?: NavigationExtras;
}

@Injectable({
    providedIn: 'root'
})
export class SectionService {
    protected router: Router;

    public constructor(protected formularService: FormularService, protected portalContextService: PortalContextService, protected languageservice: I18nService) {
        
    }

    public initRouter(router: Router) {
        this.router = router;
        return this;
    }

    /**
     * Get the current section.
     * 
     * @returns AbschnittDisplayModel | null
     */
    public current(): AbschnittDisplayModel | null {
        if (!this.router) throw new Error("Please set the router first")

        let urlSegments = this.router.url.split("/");
        let sectionId = urlSegments[urlSegments.length - 1];
        let sections = this.getSections();

        if (!sections) return null;

        let currentSection = this.searchForSection(sections, sectionId);

        return currentSection;
    }

    /**
     * ToDo: router & route has to be refactored to its own setter methods
     * 
     * @param router 
     * @param route 
     */
    public canGoBack(router: Router): boolean {
        let urlSegments = router.url.split("/");
        let sectionId = urlSegments[urlSegments.length - 1];
        let sections = this.getSections();

        if (!sections) return false;

        let currentSectionIndex = sections.findIndex(section => section.id == sectionId);

        // Cannot go back if it's the first section
        if (currentSectionIndex === 0)
            return false;
        else
            return true;
    }

    public canGoNext(router: Router): boolean {
        let urlSegments = router.url.split("/");
        let sectionId = urlSegments[urlSegments.length - 1];
        let sections = this.getSections();

        if (!sections) return false;

        let currentSectionIndex = sections.findIndex(section => section.id == sectionId);

        // Cannot go back if it's the last section. Check is only relevant if the anfrage is disabled, because DataProtection has no next button
        if (this.portalContextService.isAnfrageDisabled() && currentSectionIndex == sections.length-1)
            return false;
        else
            return true;
    }

    public previous(router: Router, route: ActivatedRoute) {
        let urlSegments = router.url.split("/");
        let sectionId = urlSegments[urlSegments.length - 1];
        let sections = this.getSections();

        if (!sections) return;

        let navigated: boolean = false;
        let currentSection = this.searchForSection(sections, sectionId);

        if (sectionId == "dataprotection") {
            let previousSection = sections[sections.length - 1]

            if (previousSection.abschnitte && previousSection.abschnitte.length > 0)
                previousSection = previousSection.abschnitte[previousSection.abschnitte.length - 1]

            if (previousSection && previousSection.formularTyp) {
                let params = this.getNavigationParamsByFormulartyp(previousSection.formularTyp, route, "../");
                params.UrlSegments.push(previousSection.id);
                router.navigate(params.UrlSegments, params.NavExtras);
                navigated = true;
            }
        }

        // Navigate to next child section
        if (currentSection && currentSection.parentId) {
            let parentSection = this.searchForSection(sections, currentSection.parentId);
            let currentSectionIndex: number = parentSection?.abschnitte?.findIndex(a => a.id == currentSection?.id) || 0;

            if (parentSection) {
                if (parentSection.abschnitte && parentSection?.abschnitte?.length > currentSectionIndex) {
                    let nextChildSection = parentSection.abschnitte[currentSectionIndex - 1];

                    if (nextChildSection && nextChildSection.formularTyp) {
                        let params = this.getNavigationParamsByFormulartyp(nextChildSection.formularTyp, route);
                        params.UrlSegments.push(nextChildSection.id);
                        router.navigate(params.UrlSegments, params.NavExtras);
                        navigated = true;
                    } else {
                        // Child -> Parent
                        let parentSectionIndex = sections.indexOf(parentSection);
                        let nextParentSection = sections[parentSectionIndex];

                        if (nextParentSection && nextParentSection.formularTyp) {
                            let params = this.getNavigationParamsByFormulartyp(nextParentSection.formularTyp, route);
                            params.UrlSegments.push(nextParentSection.id);
                            router.navigate(params.UrlSegments, params.NavExtras);
                            navigated = true;
                        }
                    }
                }
            }
        }

        // Navigate to next parent section
        if (currentSection && currentSection.parentId == null) {
            let currentSectionIndex = sections.findIndex(a => a.id == currentSection?.id);

            if (currentSectionIndex >= 0) {
                let nextSection = null

                // Parent -> Parent
                nextSection = sections[currentSectionIndex - 1];
                if (nextSection.abschnitte && nextSection.abschnitte?.length > 0) {
                    // Parent -> Last Child
                    nextSection = nextSection.abschnitte[nextSection.abschnitte.length - 1]
                }

                if (nextSection && nextSection.formularTyp) {
                    let params = this.getNavigationParamsByFormulartyp(nextSection.formularTyp, route);
                    params.UrlSegments.push(nextSection.id);
                    router.navigate(params.UrlSegments, params.NavExtras);
                    navigated = true;
                }
            }
        }

        if (!navigated) {
            router.navigate(['../../', 'dataprotection'], { relativeTo: route });
        }
    }

    public next(router: Router, route: ActivatedRoute) {
        let urlSegments = router.url.split("/");
        let sectionId = urlSegments[urlSegments.length - 1];
        let sections = this.getSections();

        if (!sections) return;

        let navigated: boolean = false;
        let currentSection = this.searchForSection(sections, sectionId);

        // Navigate to next child section
        if (currentSection && currentSection.parentId) {
            let parentSection = this.searchForSection(sections, currentSection.parentId);
            let currentSectionIndex: number = parentSection?.abschnitte?.findIndex(a => a.id == currentSection?.id) || 0;

            if (parentSection) {
                if (parentSection.abschnitte && parentSection?.abschnitte?.length > currentSectionIndex) {
                    let nextChildSection = parentSection.abschnitte[currentSectionIndex + 1];

                    if (nextChildSection && nextChildSection.formularTyp) {
                        let params = this.getNavigationParamsByFormulartyp(nextChildSection.formularTyp, route);
                        params.UrlSegments.push(nextChildSection.id);
                        router.navigate(params.UrlSegments, params.NavExtras);
                        navigated = true;
                    } else {
                        let parentSectionIndex = sections.indexOf(parentSection);
                        let nextParentSection = sections[parentSectionIndex + 1];

                        if (nextParentSection && nextParentSection.formularTyp) {
                            let params = this.getNavigationParamsByFormulartyp(nextParentSection.formularTyp, route);
                            params.UrlSegments.push(nextParentSection.id);
                            router.navigate(params.UrlSegments, params.NavExtras);
                            navigated = true;
                        }
                    }
                }
            }
        }

        // Navigate to next parent section
        if (currentSection && currentSection.parentId == null) {
            let currentSectionIndex = sections.findIndex(a => a.id == currentSection?.id);

            if (currentSectionIndex >= 0) {
                let nextSection = null

                if (currentSection.abschnitte?.length) {
                    nextSection = currentSection.abschnitte[0];
                } else {
                    nextSection = sections[currentSectionIndex + 1];
                }

                if (nextSection && nextSection.formularTyp) {
                    let params = this.getNavigationParamsByFormulartyp(nextSection.formularTyp, route);
                    params.UrlSegments.push(nextSection.id);
                    router.navigate(params.UrlSegments, params.NavExtras);
                    navigated = true;
                }
            }
        }

        if (!navigated && !this.portalContextService.isAnfrageDisabled()) {
            router.navigate(['../../', 'dataprotection'], { relativeTo: route });
        }
    }

    protected getNavigationParamsByFormulartyp(formularTyp: GlobalEnumsAceFormulartyp, route: ActivatedRoute, goBack: "../../" | "../" = "../../") {
        switch (formularTyp) {
            case GlobalEnumsAceFormulartyp.Organisation:
                return <NavigationParams>{
                    UrlSegments: [goBack, "contact-data"],
                    NavExtras: { relativeTo: route }
                }
            case GlobalEnumsAceFormulartyp.AntragstellerKontakt:
                return <NavigationParams>{
                    UrlSegments: [goBack, "applicant-form"],
                    NavExtras: { relativeTo: route }
                }
            case GlobalEnumsAceFormulartyp.Projektdetails:
                return <NavigationParams>{
                    UrlSegments: [goBack, "project-data"],
                    NavExtras: { relativeTo: route }
                }
            case GlobalEnumsAceFormulartyp.Finanzierung:
            case GlobalEnumsAceFormulartyp.FinanzierungmitFPImport:
                return <NavigationParams>{
                    UrlSegments: [goBack, "finance-data"],
                    NavExtras: { relativeTo: route }
                }
            case GlobalEnumsAceFormulartyp.Anlagen:
                return <NavigationParams>{
                    UrlSegments: [goBack, "attachments"],
                    NavExtras: { relativeTo: route }
                }
            case GlobalEnumsAceFormulartyp.Fragen:
                return <NavigationParams>{
                    UrlSegments: [goBack, "questions"],
                    NavExtras: { relativeTo: route }
                }
            case GlobalEnumsAceFormulartyp.Meilensteine:
                 return <NavigationParams>{
                    UrlSegments: [goBack, "schedule"],
                    NavExtras: { relativeTo: route }
                }
            case GlobalEnumsAceFormulartyp.DetailsStipendium:
                 return <NavigationParams>{
                    UrlSegments: [goBack, "stipendium"],
                    NavExtras: { relativeTo: route }
                }
            case GlobalEnumsAceFormulartyp.GutachtenminusBegutachtung:
                return <NavigationParams>{
                    UrlSegments: [goBack, "review-detail"],
                    NavExtras: { relativeTo: route }
                }
            default:
                return <NavigationParams>{
                    UrlSegments: [goBack, "dataprotection"],
                    NavExtras: { relativeTo: route }
                }
        }
    }

    public get FlattenSections() {
        let flatSectionList: AbschnittDisplayModel[] = [];

        if (flatSectionList.length == 0) {
            for (let x = 0; x < this.getSections().length; x++) {
                let abschnitt = this.getSections()[x];
                flatSectionList.push(abschnitt);

                if (abschnitt.id == undefined) continue;

                let subAbschnitte = this.getSubSections(abschnitt);
                for (let y = 0; y < subAbschnitte.length; y++) {
                    let subAbschnitt = subAbschnitte[y];

                    flatSectionList.push(subAbschnitt);
                }
            }
        }

        return flatSectionList;
    }

    public FlattenValidationErrors(validationErrors: { [key: string]: ValidationFailure[]; } | null): any[] {
        let validationMessages: ValidationErrors[] = [];

        if (validationErrors != null) {
            this.FlattenSections?.forEach(abschnitt => {
                if (!! abschnitt.id) {
                    validationErrors[abschnitt.id]?.forEach((validationError, i) => {
                        if (!validationError.errorMessage || !abschnitt || !abschnitt.name) return;
                        validationMessages.push(this.getValidationMessage(validationError.errorMessage, "", this.getAbschnittKurzTitel(abschnitt) ?? abschnitt.name, this.formularService.getRouteObjectThrough(abschnitt).link, i));
                    });
                }
            });
        }

        let messages = validationMessages.reduce((group: any, validationMessage) => {
            let { formName } = validationMessage;
            group[formName] = group[formName] ?? [];
            group[formName].push(validationMessage);
            return group;
        }, {});

        // I used Object.entries(...) here to not throw the order away
        return Object.entries(messages);
    }

    public getValidationMessage(errorKey: string, controlKey: string, formKey: string, formLink: string, index: number): ValidationMessage {
        return new ValidationMessage(this.getMessage(errorKey), this.getControlName(controlKey), this.getFormName(formKey), formLink, index);
    }

    public getMessage(errorKey: string): string {
        switch (errorKey) {
            case ("missingDocuments"):
                return "Fehlende Dokumente.";
            case ("required"):
                return "Fehlende Feldwerte.";
            default:
                return errorKey;
        }
    }

    public getControlName(controlKey: string): string {
        switch (controlKey) {
            default:
                return controlKey;
        }
    }

    public getFormName(formKey: string): string {
        switch (formKey) {
            case ("dataprotection"):
                return "Datenschutzerklärung";
            default:
                return formKey;
        }
    }

    public searchForSection(sections: AbschnittDisplayModel[], sectionId: string): AbschnittDisplayModel | null {
        let currentSection: AbschnittDisplayModel | null = null;

        sections.forEach(section => {
            if (currentSection == null) {
                if (section.id == sectionId)
                    currentSection = section;
                else if (section.abschnitte)
                    currentSection = this.searchForSection(section.abschnitte, sectionId);
            }
        });

        return currentSection;
    }

    protected searchForSectionIndex(sections: AbschnittDisplayModel[], sectionId: string): number | null {
        let currentSectionIndex: number | null = null;

        sections.forEach(section => {
            if (currentSectionIndex == null) {
                if (section.id == sectionId) {
                    currentSectionIndex = sections.indexOf(section);
                } else if (section.abschnitte) {
                    let foundSection = this.searchForSection(section.abschnitte, sectionId)
                    currentSectionIndex = foundSection != null ? sections.indexOf(foundSection) : this.searchForSectionIndex(section.abschnitte, sectionId);
                }
            }
        });

        return currentSectionIndex;
    }

    public getSections(): AbschnittDisplayModel[] {
        if (this.portalContextService.Abschnitte)
            return this.portalContextService.Abschnitte;
        return [];
    }

    public getSubSections(section: AbschnittDisplayModel): AbschnittDisplayModel[] {
        if (section.abschnitte)
            return section.abschnitte;

        return [];
    }

    public getAbschnittKurzTitel(section: AbschnittDisplayModel): string{
        let title: LocalizedTextDisplayModel | undefined = this.languageservice.getCurrentLocalizedTextDisplayModel(section.kurzTitel);
        return title?.text ?? "";
    }
}
